
import React from 'react';
import { MapContainer, TileLayer, Circle, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix Leaflet icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Sample station data with validation checks
const stations = [
  { id: 1, name: 'Bangkok Central', lat: 13.7563, lng: 100.5018, performance: 94, alert: false },
  { id: 2, name: 'Sukhumvit Branch', lat: 13.7308, lng: 100.5340, performance: 87, alert: false },
  { id: 3, name: 'Chiang Mai Highway', lat: 18.7883, lng: 98.9853, performance: 76, alert: true },
  { id: 4, name: 'Pattaya Main', lat: 12.9236, lng: 100.8825, performance: 92, alert: false },
  { id: 5, name: 'Phuket Beach Road', lat: 7.8804, lng: 98.3923, performance: 81, alert: true },
];

// Get color based on performance
const getPerformanceColor = (performance) => {
  if (performance >= 90) return '#34C759'; // Green
  if (performance >= 80) return '#FFCC00'; // Yellow
  return '#FF3B30'; // Red
};

const TradeAreaIntelligence = () => {
  // Validate station data before rendering
  const validStations = stations.filter(station => 
    typeof station === 'object' && 
    station !== null &&
    typeof station.lat === 'number' && 
    typeof station.lng === 'number'
  );

  // Default center coordinates if no valid stations
  const defaultCenter = [13.7563, 100.5018]; // Bangkok
  const mapCenter = validStations.length > 0 ? [validStations[0].lat, validStations[0].lng] : defaultCenter;

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-medium text-gray-900">Network Performance</h2>
          <p className="text-sm text-gray-500 mt-1">National overview of station performance and competitive pressure</p>
        </div>
        
        <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Performance summary cards */}
          <div className="col-span-1">
            <div className="space-y-4">
              <div className="bg-green-50 p-4 rounded-lg border border-green-100">
                <div className="text-sm text-gray-600">High performing stations</div>
                <div className="text-2xl font-medium text-green-700">1,254</div>
                <div className="text-xs text-green-600 mt-1">+23 from last quarter</div>
              </div>
              
              <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-100">
                <div className="text-sm text-gray-600">Average performing stations</div>
                <div className="text-2xl font-medium text-yellow-700">842</div>
                <div className="text-xs text-yellow-600 mt-1">-12 from last quarter</div>
              </div>
              
              <div className="bg-red-50 p-4 rounded-lg border border-red-100">
                <div className="text-sm text-gray-600">Low performing stations</div>
                <div className="text-2xl font-medium text-red-700">247</div>
                <div className="text-xs text-red-600 mt-1">-5 from last quarter</div>
              </div>
              
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-100">
                <div className="text-sm text-gray-600">High competitive pressure areas</div>
                <div className="text-2xl font-medium text-purple-700">129</div>
                <div className="text-xs text-purple-600 mt-1">+8 from last quarter</div>
              </div>
            </div>
          </div>
          
          {/* Map showing the stations */}
          <div className="col-span-2 h-96">
            <MapContainer 
              center={mapCenter} 
              zoom={5} 
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              
              {validStations.map(station => (
                <React.Fragment key={station.id}>
                  <Circle
                    center={[station.lat, station.lng]}
                    radius={20000}
                    pathOptions={{ 
                      fillColor: getPerformanceColor(station.performance), 
                      fillOpacity: 0.6, 
                      color: getPerformanceColor(station.performance),
                      weight: 1
                    }}
                  />
                  <Marker position={[station.lat, station.lng]}>
                    <Popup>
                      <div className="text-sm">
                        <p className="font-medium">{station.name}</p>
                        <p>Performance: {station.performance}%</p>
                        {station.alert && (
                          <p className="text-red-600 font-medium">Competition Alert!</p>
                        )}
                      </div>
                    </Popup>
                  </Marker>
                </React.Fragment>
              ))}
            </MapContainer>
          </div>
        </div>
      </div>
      
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Trade Area Insights</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
            <h4 className="text-md font-medium text-gray-800 mb-2">Competition Hotspots</h4>
            <p className="text-sm text-gray-600 mb-2">
              Top 3 areas with highest competitive pressure:
            </p>
            <ul className="list-disc pl-5 text-sm text-gray-600 space-y-1">
              <li>Bangkok Central (6 competitors within 3km)</li>
              <li>Pattaya Main Area (5 competitors within 3km)</li>
              <li>Chiang Mai Highway (4 competitors within 3km)</li>
            </ul>
          </div>
          
          <div className="p-4 bg-purple-50 rounded-lg border border-purple-100">
            <h4 className="text-md font-medium text-purple-800 mb-2">AI Recommendations</h4>
            <p className="text-sm text-gray-600 mb-2">
              Based on trade area analysis, we recommend:
            </p>
            <ul className="list-disc pl-5 text-sm text-gray-600 space-y-1">
              <li>Adjust pricing strategy in Bangkok Central</li>
              <li>Enhance non-fuel offerings in Pattaya</li>
              <li>Review station layout and accessibility in Chiang Mai</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TradeAreaIntelligence;
